import { useTranslation } from 'react-i18next';
import type { DeliveryBoardStatus } from '@interfaces/ILogistics';
import { Button } from '@components/ui/Button/Button';
import { SegmentedControl } from '@components/ui/SegmentedControl/SegmentedControl';
import { ChevronLeftIcon, ChevronRightIcon } from '@utils/icons';
import { periodLabel } from './calendar.labels';
import type { CalendarMode } from './calendar.logic';
import { CalendarLegend } from './CalendarLegend';
import type { ICalendarPeriod } from './useCalendarPeriod';
import type { ILegendFilter } from './useLegendFilter';
import styles from './CalendarHeader.module.css';

const MODES: CalendarMode[] = ['month', 'week'];

interface ICalendarHeaderProps {
  period: ICalendarPeriod;
  statuses: DeliveryBoardStatus[];
  filter: ILegendFilter;
}

/** The title, the paging, the legend and the Month/Week switch. Each legend
 *  entry narrows the calendar to its orders; several can be on at once. */
export function CalendarHeader({ period, statuses, filter }: ICalendarHeaderProps) {
  const { t, i18n } = useTranslation();
  const { mode, anchor, step, goToToday, changeMode } = period;

  return (
    <header className={styles.head}>
      <div>
        <h2 className={styles.title}>{t('logistics.calendar.title')}</h2>
        <p className={styles.subtitle}>{t('logistics.calendar.subtitle')}</p>
      </div>
      <div className={styles.nav}>
        <Button variant="ghost" size="sm" className={styles.arrow} aria-label={t('logistics.calendar.previous')} onClick={() => step(-1)}>
          <ChevronLeftIcon />
        </Button>
        <span className={styles.period}>{periodLabel(mode, anchor, i18n.language)}</span>
        <Button variant="ghost" size="sm" className={styles.arrow} aria-label={t('logistics.calendar.next')} onClick={() => step(1)}>
          <ChevronRightIcon />
        </Button>
      </div>
      <Button variant="secondary" size="sm" onClick={goToToday}>{t('logistics.calendar.today')}</Button>
      <div className={styles.end}>
        <CalendarLegend statuses={statuses} filter={filter} />
        <SegmentedControl
          value={mode}
          onChange={(next) => { if (next !== mode) changeMode(next); }}
          options={MODES.map((id) => ({ id, label: t(`logistics.calendar.${id}`) }))}
          ariaLabel={t('logistics.calendar.viewMode')}
          size="sm"
        />
      </div>
    </header>
  );
}
